import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, Copy, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/errors";
import { ukDateTime } from "../../lib/format";
import { Card, Field, Input, PrimaryButton, QuietButton, Select } from "../../components/UI";

/** LT-38 — Tender → Output → Send email. Compose a request-for-quote from a template and open it in Outlook via mailto. */
export function EmailTab({ tender }) {
  const [search] = useSearchParams();
  const [subs, setSubs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [trades, setTrades] = useState([]);
  const [subId, setSubId] = useState(search.get("subcontractor") || "");
  const [templateId, setTemplateId] = useState("");
  const [tradeId, setTradeId] = useState("");
  const [returnBy, setReturnBy] = useState("");
  const [preview, setPreview] = useState(null);      // server render
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [edited, setEdited] = useState(false);
  const [log, setLog] = useState([]);

  useEffect(() => {
    api.get("/subcontractors").then((r) => setSubs(r.data || [])).catch(() => {});
    api.get("/email-templates").then((r) => { const t = r.data || []; setTemplates(t); if (t[0]) setTemplateId((cur) => cur || t[0].id); }).catch(() => {});
    api.get("/labour-rates").then((r) => setTrades(r.data || [])).catch(() => {});
  }, []);
  const loadLog = useCallback(() => { api.get(`/tenders/${tender.id}/email/log`).then((r) => setLog(r.data.items || [])).catch(() => {}); }, [tender.id]);
  useEffect(loadLog, [loadLog]);

  // Re-render from the template whenever an input changes, unless the user has edited the text by hand.
  useEffect(() => {
    if (!subId || !templateId) { setPreview(null); return; }
    const payload = { subcontractor_id: subId, template_id: templateId, trade_id: tradeId || undefined, return_by: returnBy || undefined };
    if (edited) { payload.subject = subject; payload.body = body; }
    api.post(`/tenders/${tender.id}/email/render`, payload)
      .then((r) => { setPreview(r.data); if (!edited) { setSubject(r.data.subject); setBody(r.data.body); } })
      .catch((e) => toast.error(formatApiError(e, "Could not build the email")));
  }, [subId, templateId, tradeId, returnBy, tender.id]); // eslint-disable-line

  const sub = subs.find((s) => s.id === subId);
  const to = sub?.contact_email || "";
  const mailto = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const over = body.length > (preview?.soft_limit || 1500);
  const tpl = templates.find((t) => t.id === templateId);

  const logIt = () => api.post(`/tenders/${tender.id}/email/log`, { subcontractor_id: subId, to, subject, template_id: templateId || null, template_name: tpl?.name || null, body_length: body.length }).then(loadLog).catch(() => {});
  const openOutlook = () => {
    if (!to) { toast.error("This subcontractor has no email address — add one in the Directory."); return; }
    logIt();
    window.location.href = mailto;
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(`To: ${to}\nSubject: ${subject}\n\n${body}`); toast.success("Copied — paste into any email"); logIt(); }
    catch { toast.error("Copy failed"); }
  };
  const resetToTemplate = () => { setEdited(false); if (preview) { setSubject(preview.subject); setBody(preview.body); } setTradeId((t) => t); };

  const subOptions = subs.map((s) => ({ value: s.id, label: s.company_name + (s.contact_email ? "" : " · no email") }));
  const tplOptions = templates.map((t) => ({ value: t.id, label: t.name }));
  const tradeOptions = [{ value: "", label: sub?.trade_names?.[0] ? `Subby's trade (${sub.trade_names[0]})` : "— none —" }, ...trades.map((t) => ({ value: t.id, label: t.trade_name }))];

  return (
    <div className="mt-6 grid gap-5" style={{ gridTemplateColumns: "340px 1fr" }} data-testid="email-tab">
      <Card className="p-5">
        <h3 className="font-display text-ink mb-3" style={{ fontSize: 15, fontWeight: 600 }}>Send email</h3>
        <div className="grid gap-4">
          <div data-testid="email-tab-subcontractor">
            <Field label="To (subcontractor)" required hint={subs.length === 0 ? <span>Directory is empty — <Link to="/labour-rates?tab=directory" className="underline">add one</Link>.</span> : undefined}>
              <Select value={subId} onChange={(v) => { setSubId(v); setEdited(false); }} options={subOptions} placeholder="Pick from the Directory…" />
            </Field>
          </div>
          <div data-testid="email-tab-template">
            <Field label="Template" hint={<Link to="/email-templates" className="underline">Manage templates</Link>}>
              <Select value={templateId} onChange={(v) => { setTemplateId(v); setEdited(false); }} options={tplOptions} placeholder="Pick a template…" />
            </Field>
          </div>
          <div data-testid="email-tab-trade">
            <Field label="Trade">
              <Select value={tradeId} onChange={setTradeId} options={tradeOptions} />
            </Field>
          </div>
          <Field label="Return-by date" hint="Fills {return_by}">
            <Input value={returnBy} onChange={setReturnBy} placeholder="e.g. Friday 24/09" data-testid="email-tab-return-by" />
          </Field>
        </div>
        {preview?.empty_fields?.length > 0 && (
          <div className="mt-4 rounded-md border px-3 py-2 flex items-start gap-2" style={{ background: "#FEF3E2", borderColor: "#F2DFB8", color: "#7A4C0A", fontSize: 11.5 }} data-testid="email-tab-empty-fields">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            <span>Blank in this email: {preview.empty_fields.map((f) => `{${f}}`).join(", ")} — fill them in or edit the text.</span>
          </div>
        )}
      </Card>

      <Card className="p-5">
        {!subId || !templateId ? (
          <div className="text-ink-3 py-10 text-center" style={{ fontSize: 12.5 }} data-testid="email-tab-placeholder">Pick a subcontractor and a template to build the email.</div>
        ) : (
          <div className="grid gap-3" data-testid="email-tab-preview">
            <div className="flex items-center gap-2" style={{ fontSize: 12.5 }}>
              <span className="col-head" style={{ fontSize: 9.5, width: 60 }}>To</span>
              <span className={to ? "text-ink font-mono" : "text-note-red"} data-testid="email-tab-to">{to || "No email on this subcontractor"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="col-head" style={{ fontSize: 9.5, width: 60 }}>Subject</span>
              <input value={subject} onChange={(e) => { setSubject(e.target.value); setEdited(true); }} data-testid="email-tab-subject"
                className="flex-1 h-9 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent" style={{ fontSize: 13 }} />
            </div>
            <textarea value={body} onChange={(e) => { setBody(e.target.value); setEdited(true); }} rows={14} data-testid="email-tab-body"
              className="px-3 py-2 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-y" style={{ fontSize: 13, lineHeight: "19px" }} />
            <div className="flex items-center justify-between gap-3">
              <div className="text-ink-3" style={{ fontSize: 11.5 }} data-testid="email-tab-length">
                <span className={over ? "text-note-red font-medium" : ""}>{body.length.toLocaleString("en-GB")} characters</span>
                {over && <span className="text-note-red"> — over {preview?.soft_limit || 1500}; Outlook may truncate a mailto this long. Trim it or Copy instead.</span>}
                {edited && <button type="button" onClick={resetToTemplate} className="ml-2 underline hover:text-ink" data-testid="email-tab-reset">Reset to template</button>}
              </div>
              <div className="flex items-center gap-2">
                <QuietButton icon={Copy} onClick={copy} data-testid="email-tab-copy">Copy</QuietButton>
                <PrimaryButton icon={ExternalLink} onClick={openOutlook} disabled={!to} data-testid="email-tab-open">Open in Outlook</PrimaryButton>
              </div>
            </div>
            <a href={mailto} className="hidden" data-testid="email-tab-mailto">mailto</a>
            <div className="text-ink-4" style={{ fontSize: 10.5 }}>Opens your own mail app with the text filled in — nothing is sent from LuxTender. Plain text only; attach drawings in Outlook.</div>
          </div>
        )}
        <div className="mt-5 border-t border-line pt-4">
          <div className="col-head mb-2" style={{ fontSize: 9.5 }}>Recently opened</div>
          {log.length === 0 ? <div className="text-ink-4" style={{ fontSize: 12 }} data-testid="email-tab-log-empty">Nothing yet.</div> : (
            <ul className="space-y-1" data-testid="email-tab-log">
              {log.map((l, i) => (
                <li key={i} className="flex items-center gap-2 text-ink-2" style={{ fontSize: 12 }}>
                  <Mail size={11} className="text-ink-3" />
                  <span className="font-medium text-ink">{l.subcontractor_name || l.to}</span>
                  <span className="truncate">— {l.subject}</span>
                  <span className="ml-auto text-ink-3 flex-shrink-0">{l.template_name ? `${l.template_name} · ` : ""}{ukDateTime(l.at)}{l.by ? ` · ${l.by}` : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}
